import { gradientCss } from "../native/gradient";
import { inputGradient, syncPaintInput } from "./native-paint-input";
import { originalIcon } from "./native-original-icons";
type Descriptor = {
  id?: string;
  icon: string;
  title: string;
  choices?: [string, string, string][];
  exclusive?: boolean;
};
export function wrapOriginalSection(
  id: string,
  groups: {
    title: string;
    icon: string;
    selectors: string[];
    open?: boolean;
  }[],
) {
  const panel = document.getElementById(id)!;
  panel.querySelector("h3")!.hidden = true;
  for (const g of groups) {
    const details = document.createElement("details");
    details.className = "native-properties";
    details.open = !!g.open;
    const summary = document.createElement("summary");
    summary.innerHTML = originalIcon(g.icon);
    summary.append(document.createTextNode(g.title));
    const body = document.createElement("div");
    body.className = "native-property-body";
    for (const selector of g.selectors) {
      const child = panel.querySelector(selector);
      if (child) body.append(child);
    }
    details.append(summary, body);
    panel.append(details);
  }
}
/** Source controls own values and commands; compact proxies only forward browser input. */
export function originalCompact(id: string, descriptors: Descriptor[]) {
  const row = document.createElement("div");
  row.id = id;
  row.className = "native-property-row";
  row.hidden = true;
  const close = () =>
    row
      .querySelectorAll<HTMLElement>(".native-property-popup")
      .forEach((p) => (p.hidden = true));
  for (const d of descriptors) {
    const anchor = document.createElement("div");
    anchor.className = "native-property-anchor";
    const trigger = document.createElement("button");
    trigger.type = "button";
    trigger.title = trigger.ariaLabel = d.title;
    trigger.innerHTML = originalIcon(d.icon);
    trigger.dataset.originalControl = d.id ?? d.title;
    if (d.exclusive) trigger.dataset.originalExclusive = "true";
    // Prevent mouse focus changes without cancelling touch activation in WebKit.
    trigger.onmousedown = (e) => e.preventDefault();
    anchor.append(trigger);
    const source = d.id
      ? (document.getElementById(d.id) as HTMLInputElement)
      : null;
    if (source?.tagName === "BUTTON") {
      trigger.onclick = () => {
        close();
        source.click();
      };
    } else {
      const popup = document.createElement("div");
      popup.className = "native-property-popup";
      popup.hidden = true;
      anchor.append(popup);
      trigger.onclick = () => {
        const open = popup.hidden;
        close();
        popup.hidden = !open;
        const r = popup.getBoundingClientRect();
        popup.style.marginLeft =
          Math.max(8 - r.left, Math.min(0, innerWidth - r.right - 8)) + "px";
      };
      if (d.choices) {
        for (const [id, icon, title] of d.choices) {
          const b = document.createElement("button");
          b.type = "button";
          b.dataset.originalChoice = id;
          b.innerHTML = originalIcon(icon);
          b.title = b.ariaLabel = title;
          b.onclick = () => {
            document.getElementById(id)!.click();
            close();
          };
          popup.append(b);
        }
      } else if (source) {
        if (source.getAttribute("type") === "color") {
          trigger.dataset.colorInput = source.id;
          trigger.onclick = close;
        }
        const clone = source.cloneNode(true) as HTMLInputElement;
        clone.id = "compact-" + source.id;
        clone.dataset.originalSource = source.id;
        clone.setAttribute("aria-label", d.title);
        const forward = (type: string) => {
          source.value = clone.value;
          if (source.type === "checkbox") source.checked = clone.checked;
          source.dispatchEvent(new Event(type, { bubbles: true }));
        };
        clone.oninput = () => forward("input");
        clone.onchange = () => forward("change");
        popup.append(clone);
        if (source.type === "number" || source.type === "range") {
          const slider = document.createElement("input");
          slider.type = "range";
          slider.min = source.min || "0";
          slider.max = source.max || "200";
          slider.step = source.step || "1";
          slider.value = source.value;
          slider.className = "native-original-vertical";
          slider.setAttribute("aria-label", d.title);
          slider.dataset.originalSource = source.id;
          slider.oninput = () => {
            clone.value = slider.value;
            forward("input");
          };
          slider.onchange = () => forward("change");
          popup.append(slider);
        }
        if (source.tagName === "SELECT")
          clone.onchange = () => {
            forward("change");
            close();
          };
      }
    }
    row.append(anchor);
  }
  row.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      const popup = (e.target as HTMLElement).closest<HTMLElement>(
        ".native-property-popup",
      );
      if (popup) {
        e.stopPropagation();
        const id = (e.target as HTMLElement).dataset.originalSource;
        if (id)
          document
            .getElementById(id)
            ?.dispatchEvent(
              new KeyboardEvent("keydown", { key: "Escape", bubbles: true }),
            );
        popup.hidden = true;
      }
    }
  });
  document.addEventListener(
    "pointerdown",
    (e) => {
      if (!row.contains(e.target as Node)) close();
    },
    true,
  );
  document.getElementById("native-context-properties")!.prepend(row);
  return row;
}
export function syncOriginalCompact(id: string) {
  for (const choice of document.querySelectorAll<HTMLButtonElement>(
    `#${id} [data-original-choice]`,
  )) {
    const source = document.getElementById(
      choice.dataset.originalChoice!,
    ) as HTMLButtonElement;
    const pressed = source.getAttribute("aria-pressed") ?? "false";
    choice.disabled = source.disabled;
    choice.setAttribute("aria-pressed", pressed);
    choice.classList.toggle("active", pressed === "true");
  }
  for (const trigger of document.querySelectorAll<HTMLButtonElement>(
    `#${id} [data-original-exclusive]`,
  )) {
    const active = trigger.parentElement!.querySelector<HTMLButtonElement>(
      '[data-original-choice][aria-pressed="true"]',
    );
    if (active && trigger.innerHTML !== active.innerHTML)
      trigger.innerHTML = active.innerHTML;
    trigger.dataset.currentChoice = active?.dataset.originalChoice ?? "mixed";
  }
  for (const trigger of document.querySelectorAll<HTMLButtonElement>(
    `#${id} [data-original-control]`,
  )) {
    const source = document.getElementById(trigger.dataset.originalControl!) as
      HTMLButtonElement | HTMLInputElement | null;
    if (source) {
      trigger.disabled = source.disabled;
      if (
        source instanceof HTMLInputElement &&
        source.getAttribute("type") === "color"
      ) {
        const gradient = inputGradient(source);
        trigger.classList.add("native-color-button");
        trigger.style.setProperty(
          "--swatch",
          gradient ? gradientCss(gradient) : source.value,
        );
      }
    }
  }
  for (const proxy of document.querySelectorAll<HTMLInputElement>(
    `#${id} [data-original-source]`,
  )) {
    const source = document.getElementById(
      proxy.dataset.originalSource!,
    ) as HTMLInputElement;
    if (
      proxy instanceof HTMLSelectElement &&
      source instanceof HTMLSelectElement &&
      proxy.innerHTML !== source.innerHTML
    )
      proxy.innerHTML = source.innerHTML;
    if (document.activeElement !== proxy) proxy.value = source.value;
    if (source.type === "checkbox") proxy.checked = source.checked;
    proxy.disabled = source.disabled;
    if (
      source.getAttribute("type") === "color" &&
      source.dataset.gradientSupported
    )
      syncPaintInput(proxy, inputGradient(source));
  }
}
